import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, Empty } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { fmtDate, money } from "@/lib/format";

const REPORTS = [
  { code: "trial-balance", name: "Trial Balance", module: "Financial" },
  { code: "profit-loss", name: "Profit & Loss", module: "Financial" },
  { code: "balance-sheet", name: "Balance Sheet", module: "Financial" },
  { code: "day-book", name: "Day Book", module: "Financial" },
  { code: "receivables", name: "Receivables Ageing", module: "AR" },
  { code: "payables", name: "Payables Ageing", module: "AP" },
  { code: "stock-summary", name: "Stock Summary", module: "Inventory" },
];

export default function Reports() {
  const [active, setActive] = useState("trial-balance");
  const [data, setData] = useState(null);
  const [range, setRange] = useState({ from: "", to: "" });

  const load = async (code) => {
    setData(null);
    const qs = new URLSearchParams();
    if (range.from) qs.set("from_date", range.from);
    if (range.to) qs.set("to_date", range.to);
    const url = {
      "trial-balance": `/reports/trial-balance?${qs}`,
      "profit-loss": `/reports/profit-loss?${qs}`,
      "balance-sheet": `/reports/balance-sheet?${qs}`,
      "day-book": `/reports/day-book?${qs}`,
      receivables: "/reports/outstanding?kind=receivable",
      payables: "/reports/outstanding?kind=payable",
      "stock-summary": "/inventory/stock-summary",
    }[code];
    const { data: d } = await api.get(url);
    setData(d);
  };

  useEffect(() => { load(active); /* eslint-disable-next-line */ }, [active, range]);

  const exportCsv = () => {
    const flat = Array.isArray(data) ? data : data?.rows || data?.income || [];
    if (!flat.length) return;
    const keys = Object.keys(flat[0]).filter((k) => typeof flat[0][k] !== "object");
    const csv = [keys.join(","), ...flat.map((r) => keys.map((k) => `"${r[k] ?? ""}"`).join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `${active}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <PageHeader title="Reports" subtitle="Statutory and management reports rendered from the same posted ledger.">
        <Input type="date" className="h-8 w-36 text-xs" value={range.from} data-testid="report-from-date"
          onChange={(e) => setRange({ ...range, from: e.target.value })} />
        <Input type="date" className="h-8 w-36 text-xs" value={range.to} data-testid="report-to-date"
          onChange={(e) => setRange({ ...range, to: e.target.value })} />
        <Button size="sm" variant="outline" onClick={exportCsv} data-testid="export-csv-btn">Export CSV</Button>
      </PageHeader>

      <div className="flex flex-wrap gap-1 border-b border-border pb-2">
        {REPORTS.map((r) => (
          <button key={r.code} onClick={() => { setData(null); setActive(r.code); }} data-testid={`report-tab-${r.code}`}
            className={`px-2.5 py-1 text-xs ${active === r.code ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}
            style={{ transitionProperty: "background-color, color" }}>
            {r.name}
          </button>
        ))}
      </div>

      {!data ? <p className="text-sm text-muted-foreground">Running report…</p> : (
        <div className="border border-border" data-testid={`report-${active}`}>
          {active === "trial-balance" && data.rows && (
            <table className="dense-table w-full">
              <thead><tr><th>Ledger</th><th>Group</th><th className="text-right">Opening</th>
                <th className="text-right">Debit</th><th className="text-right">Credit</th><th className="text-right">Closing</th></tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.ledger_id} className="row-in">
                    <td className="font-medium">{r.name}</td>
                    <td className="text-muted-foreground">{r.group}</td>
                    <td className="num text-right">{money(r.opening)} {r.opening_dc}</td>
                    <td className="num text-right">{money(r.debit)}</td>
                    <td className="num text-right">{money(r.credit)}</td>
                    <td className="num text-right font-medium">{money(r.closing)} {r.closing_dc}</td>
                  </tr>
                ))}
                <tr className="bg-muted/40 font-semibold">
                  <td colSpan={3}>Total</td>
                  <td className="num text-right">{money(data.total_debit)}</td>
                  <td className="num text-right">{money(data.total_credit)}</td>
                  <td className={`text-right text-xs ${data.balanced ? "text-success" : "text-destructive"}`}>
                    {data.balanced ? "Balanced" : "Out of balance"}
                  </td>
                </tr>
              </tbody>
            </table>
          )}

          {active === "profit-loss" && data.income && (
            <div className="grid gap-px bg-border md:grid-cols-2">
              {[["Expenses", data.expense, data.total_expense], ["Income", data.income, data.total_income]].map(([title, rows, total]) => (
                <div key={title} className="bg-background">
                  <p className="border-b border-border px-4 py-2 font-display text-sm font-bold">{title}</p>
                  <table className="dense-table w-full">
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.name}><td>{r.name}</td><td className="num text-right">{money(r.amount)}</td></tr>
                      ))}
                      <tr className="bg-muted/40 font-semibold"><td>Total</td><td className="num text-right">{money(total)}</td></tr>
                    </tbody>
                  </table>
                </div>
              ))}
              <div className="bg-background p-4 md:col-span-2">
                <div className="flex flex-wrap gap-8">
                  {[["Gross profit", data.gross_profit], ["Net profit", data.net_profit], ["Margin", `${data.margin_pct}%`]].map(([k, v]) => (
                    <div key={k}>
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</p>
                      <p className="num text-lg font-semibold">{typeof v === "number" ? money(v) : v}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {active === "balance-sheet" && data.assets && (
            <div className="grid gap-px bg-border md:grid-cols-2">
              {[["Liabilities & Equity", data.liabilities, data.total_liabilities], ["Assets", data.assets, data.total_assets]].map(([title, rows, total]) => (
                <div key={title} className="bg-background">
                  <p className="border-b border-border px-4 py-2 font-display text-sm font-bold">{title}</p>
                  <table className="dense-table w-full">
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.name}>
                          <td>{r.name}<span className="ml-2 text-[10px] text-muted-foreground">{r.group}</span></td>
                          <td className="num text-right">{money(r.amount)}</td>
                        </tr>
                      ))}
                      <tr className="bg-muted/40 font-semibold"><td>Total</td><td className="num text-right">{money(total)}</td></tr>
                    </tbody>
                  </table>
                </div>
              ))}
              <p className={`bg-background p-3 text-xs md:col-span-2 ${data.balanced ? "text-success" : "text-destructive"}`} data-testid="bs-balance-status">
                {data.balanced ? "Assets = Liabilities + Equity" : `Difference ${money(data.difference)}`} · {data.note}
              </p>
            </div>
          )}

          {active === "day-book" && Array.isArray(data) && (
            data.length === 0 ? <Empty label="No vouchers in range" /> : (
              <table className="dense-table w-full">
                <thead><tr><th>Date</th><th>Type</th><th>Number</th><th>Party / Narration</th><th className="text-right">Amount</th><th>Status</th></tr></thead>
                <tbody>
                  {data.map((v) => (
                    <tr key={v.id} className="row-in">
                      <td className="num">{fmtDate(v.date)}</td><td>{v.type}</td><td className="num">{v.number}</td>
                      <td className="max-w-[320px] truncate">{v.party_name || v.narration || "—"}</td>
                      <td className="num text-right">{money(v.amount)}</td><td className="text-muted-foreground">{v.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}

          {(active === "receivables" || active === "payables") && data.aging && (
            <>
              <div className="grid grid-cols-4 gap-px bg-border">
                {data.aging.map((a) => (
                  <div key={a.bucket} className="bg-background p-3">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{a.bucket} days</p>
                    <p className="num mt-1 font-semibold">{money(a.amount)}</p>
                  </div>
                ))}
              </div>
              <table className="dense-table w-full">
                <thead><tr><th>Party</th><th>GSTIN</th><th>Last txn</th><th className="text-right">Age</th><th className="text-right">Amount</th></tr></thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.ledger_id} className="row-in">
                      <td className="font-medium">{r.name}</td>
                      <td className="num">{r.gstin || "—"}</td>
                      <td className="num">{r.last_txn ? fmtDate(r.last_txn) : "—"}</td>
                      <td className="num text-right">{r.age_days} d</td>
                      <td className="num text-right font-medium">{money(r.amount)}</td>
                    </tr>
                  ))}
                  <tr className="bg-muted/40 font-semibold"><td colSpan={4}>Total</td><td className="num text-right">{money(data.total)}</td></tr>
                </tbody>
              </table>
            </>
          )}

          {active === "stock-summary" && data.total_value !== undefined && (
            <table className="dense-table w-full">
              <thead><tr><th>Item</th><th className="text-right">Closing</th><th className="text-right">Avg rate</th><th className="text-right">Value</th></tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.item_id} className="row-in">
                    <td>{r.name}</td><td className="num text-right">{r.closing_qty}</td>
                    <td className="num text-right">{money(r.avg_rate)}</td>
                    <td className="num text-right font-medium">{money(r.value)}</td>
                  </tr>
                ))}
                <tr className="bg-muted/40 font-semibold"><td colSpan={3}>Total stock value</td><td className="num text-right">{money(data.total_value)}</td></tr>
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
